import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { comics, categories } from '@/mocks/comics';

function generateChapters(totalChapters: number) {
  return Array.from({ length: Math.min(totalChapters, 30) }, (_, i) => {
    const num = totalChapters - i;
    return {
      number: num,
      title: `第${num}话`,
      updatedAt: i === 0 ? '刚刚' : `${i + 1}天前`,
    };
  });
}

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [showAllChapters, setShowAllChapters] = useState(false);

  const comic = comics.find((c) => c.id === Number(id));

  const categoryName = useMemo(() => {
    if (!comic) return '';
    const cat = categories.find((c) => c.slug === comic.category);
    return cat?.name || '其他';
  }, [comic]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.category === comic.category && c.id !== comic.id)
      .slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <SEO title="404" description="找不到该漫画" />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 font-heading">漫画未找到</h1>
            <p className="text-foreground-500 mt-2">抱歉，该漫画暂时不可用</p>
            <Link to="/" className="inline-block mt-4 px-6 py-2 bg-primary-500 text-white rounded-full text-sm cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const chapters = generateChapters(comic.chapters);
  const displayedChapters = showAllChapters ? chapters : chapters.slice(0, 12);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://gxzzmm66.cn/' },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `http://gxzzmm66.cn/category/${comic.category}` },
      { '@type': 'ListItem', position: 3, name: comic.title, item: `http://gxzzmm66.cn/comic/${comic.id}` },
    ],
  };

  const bookSchema = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    genre: categoryName,
    url: `http://gxzzmm66.cn/comic/${comic.id}`,
    image: comic.coverImage,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: 10,
      ratingCount: Math.floor(comic.views / 1000),
    },
    review: {
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: comic.rating,
        bestRating: 10,
      },
      author: { '@type': 'Person', name: '韩漫网用户' },
      reviewBody: comic.description.slice(0, 100),
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}更新到多少话了？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${comic.title}》目前已更新至第${comic.chapters}话，${comic.status === 'completed' ? '已完结' : '持续连载中'}，每周在韩漫网同步更新最新章节。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}的作者是谁？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${comic.title}》的作者为${comic.author}，是一位深受读者喜爱的韩国漫画家，作品以${comic.tags[0]}风格著称。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}是什么类型的漫画？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${comic.title}》是一部${categoryName}类型的韩国漫画，主要标签为${comic.tags.join('、')}。`,
        },
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${comic.title} - ${comic.author} - ${categoryName}韩漫`}
        description={comic.description}
        keywords={`${comic.title},${comic.author},${categoryName},韩漫${comic.title},免费看${comic.title}`}
        canonicalUrl={`http://gxzzmm66.cn/comic/${comic.id}`}
        ogType="book"
        ogImage={comic.coverImage}
        schemaData={{ ...breadcrumbSchema, ...bookSchema, ...faqSchema }}
      />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto py-6">
          <nav aria-label="面包屑导航">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li><Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer">首页</Link></li>
              <li><div className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-foreground-400"></i></div></li>
              <li><Link to={`/category/${comic.category}`} className="hover:text-foreground-800 transition-colors cursor-pointer">{categoryName}</Link></li>
              <li><div className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-foreground-400"></i></div></li>
              <li className="text-foreground-800 font-medium">{comic.title}</li>
            </ol>
          </nav>
        </div>

        {/* Comic Info */}
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8 mb-12">
            {/* Cover */}
            <div className="w-full md:w-64 lg:w-72 flex-shrink-0">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-background-200">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <Link
                  to={`/category/${comic.category}`}
                  className="px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-medium cursor-pointer"
                >
                  {categoryName}
                </Link>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  comic.status === 'completed' ? 'bg-secondary-100 text-secondary-900' : 'bg-primary-100 text-primary-700'
                }`}>
                  {comic.status === 'completed' ? '已完结' : '连载中'}
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 flex items-center justify-center"><i className="ri-star-fill text-accent-500"></i></div>
                  <span className="text-lg font-bold text-foreground-900">{comic.rating}</span>
                  <span className="text-xs text-foreground-400">/10</span>
                </div>
                <div className="w-px h-6 bg-background-300"></div>
                <div className="flex items-center gap-1.5 text-sm text-foreground-600">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-eye-line text-foreground-400 text-xs"></i></div>
                  {(comic.views / 10000).toFixed(0)}万阅
                </div>
                <div className="w-px h-6 bg-background-300"></div>
                <div className="flex items-center gap-1.5 text-sm text-foreground-600">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-book-3-line text-foreground-400 text-xs"></i></div>
                  {comic.chapters}话
                </div>
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-5 max-w-xl">
                {comic.description}
              </p>

              <div className="flex flex-wrap gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-200 text-foreground-600 text-xs font-medium">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <button className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-play-fill text-sm"></i></div>
                  开始阅读
                </button>
                <button className="inline-flex items-center gap-2 px-6 py-3 bg-background-200 text-foreground-800 rounded-full text-sm font-semibold hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-bookmark-line text-sm"></i></div>
                  加入书架
                </button>
              </div>
            </div>
          </div>

          {/* Chapter List */}
          <div className="mb-12">
            <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">章节列表</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {displayedChapters.map((ch) => (
                <button
                  key={ch.number}
                  className="text-left p-3 rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
                >
                  <p className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors">{ch.title}</p>
                  <p className="text-xs text-foreground-400 mt-1">{ch.updatedAt}</p>
                </button>
              ))}
            </div>
            {chapters.length > 12 && !showAllChapters && (
              <div className="text-center mt-5">
                <button
                  onClick={() => setShowAllChapters(true)}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-foreground-200 text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  展开全部 {comic.chapters} 话
                  <i className="ri-arrow-down-s-line"></i>
                </button>
              </div>
            )}
          </div>

          {/* Related */}
          {relatedComics.length > 0 && (
            <div className="mb-16">
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">同类推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-200">
                      <img
                        src={rc.coverImage}
                        alt={rc.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">{rc.title}</h3>
                    <p className="text-xs text-foreground-500 mt-1">{rc.author}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="flex items-center gap-0.5 text-xs text-accent-600">
                        <i className="ri-star-fill text-[10px]"></i>{rc.rating}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}